import { useMemo } from 'react';
import { ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { IsoDate } from '@/features/manufacturing/planning/dates';
import type { VendorOrderGroup } from '@/features/manufacturing/planning/procurementCalendar';
import type { CalendarEvent, PlanningModel } from '@/features/manufacturing/planning/types';
import { formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { EventRow, weekdayDate } from './shared';
import { VendorOrderCard } from './VendorOrderCard';

interface ProcurementAgendaProps {
  model: PlanningModel;
  from: IsoDate;
  to: IsoDate;
  groupsByDay: Map<IsoDate, VendorOrderGroup[]>;
  eventsByDay: Map<IsoDate, CalendarEvent[]>;
  canCreate: boolean;
  onCreatePo: (group: VendorOrderGroup) => void;
  onCreateDay: (date: IsoDate, groups: VendorOrderGroup[]) => void;
  onOpenItem: (itemId: string) => void;
  emptyText: string;
}

/**
 * The same month as a day-by-day list: what to order (with its Create PO
 * button), then the deadlines, arrivals and run-outs of that day. This is
 * the whole calendar on a phone and the "List" view on desktop.
 */
export function ProcurementAgenda({
  model,
  from,
  to,
  groupsByDay,
  eventsByDay,
  canCreate,
  onCreatePo,
  onCreateDay,
  onOpenItem,
  emptyText,
}: ProcurementAgendaProps) {
  const days = useMemo(() => {
    const dates = new Set<IsoDate>();
    for (const d of groupsByDay.keys()) if (d >= from && d <= to) dates.add(d);
    for (const d of eventsByDay.keys()) if (d >= from && d <= to) dates.add(d);
    return [...dates].sort();
  }, [groupsByDay, eventsByDay, from, to]);

  if (days.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border px-4 py-12 text-center text-sm text-muted-foreground">
        {emptyText}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {days.map((date) => {
        const groups = groupsByDay.get(date) ?? [];
        const events = eventsByDay.get(date) ?? [];
        const draftable = groups.filter((g) => !g.unassignable);
        const total = groups.reduce((acc, g) => acc + g.totalCents, 0);
        const isToday = date === model.today;
        return (
          <section key={date} className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'flex h-9 min-w-9 items-center justify-center rounded-full px-2 text-sm font-semibold tabular-nums',
                    isToday ? 'bg-info text-linen' : 'bg-secondary text-foreground',
                  )}
                >
                  {Number(date.slice(8, 10))}
                </span>
                <div>
                  <div className="text-sm font-medium">
                    {weekdayDate(date)}
                    {isToday && <span className="ml-2 text-xs font-normal text-info">Today</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {model.calendar.isHoliday(date) ? 'Holiday' : model.calendar.isWorkingDay(date) ? 'Working day' : 'Non-working day'}
                    {groups.length > 0 && ` · ${groups.length} ${groups.length === 1 ? 'vendor order' : 'vendor orders'}`}
                    {total > 0 && ` · ${formatCurrency(total)}`}
                  </div>
                </div>
              </div>
              {canCreate && draftable.length > 1 && (
                <Button type="button" size="sm" variant="outline" onClick={() => onCreateDay(date, draftable)}>
                  <ClipboardList className="h-3.5 w-3.5" />
                  Create all {draftable.length} draft POs
                </Button>
              )}
            </div>
            {groups.map((group) => (
              <VendorOrderCard
                key={group.key}
                group={group}
                model={model}
                canCreate={canCreate}
                onCreatePo={onCreatePo}
                onOpenItem={onOpenItem}
              />
            ))}
            {events.length > 0 && (
              <ul className="divide-y divide-border rounded-xl border border-border bg-card px-4">
                {events.map((event) => (
                  <EventRow key={event.id} event={event} onOpenItem={onOpenItem} />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
